import { Capacitor } from "@capacitor/core";
import {
  BadgeCheck, Bluetooth, BookOpen, ChevronRight, CircleHelp, CloudOff, Copy, Globe2,
  Headphones, Languages, Link2, LogOut, MessageSquareText, Mic2, Network, QrCode,
  Settings2, ShieldCheck, Smartphone, Sparkles, Trash2, UserRound,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { apiRequest, ApiError } from "../lib/api";
import { NativeSpeech } from "../lib/nativeSpeech";

interface NativeDeviceStatus {
  voiceCommandsEnabled: boolean;
  bluetoothConnected: boolean;
  audioOutputs: string[];
}

export function ProfilePage() {
  const navigate = useNavigate();
  const [showDevice, setShowDevice] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [copied, setCopied] = useState(false);
  const [nativeStatus, setNativeStatus] = useState<NativeDeviceStatus>({ voiceCommandsEnabled: true, bluetoothConnected: false, audioOutputs: [] });
  const { account, token, logout } = useAuth();
  const isNative = Capacitor.isNativePlatform();
  const usage = account?.usage.characters ?? 0;
  const limit = account?.limits.monthlyCharacters ?? 1;
  const usagePercent = Math.min(100, Math.round((usage / Math.max(limit, 1)) * 100));
  const speechUsed = account?.limits.speechSessionsUsed ?? 0;
  const speechLimit = account?.limits.dailySpeechSessions ?? 4;
  const speechRemaining = Math.max(0, speechLimit - speechUsed);
  const deviceId = account?.license?.deviceId ?? "";
  const shortDeviceId = useMemo(() => deviceId ? `${deviceId.slice(0, 7)}•••${deviceId.slice(-5)}` : "Sin vincular", [deviceId]);

  useEffect(() => {
    if (!isNative) return;
    void NativeSpeech.getDeviceStatus().then(setNativeStatus).catch(() => undefined);
  }, [isNative]);

  function leave() { logout(); navigate("/", { replace: true }); }

  async function copyDeviceId() {
    if (!deviceId) return;
    await navigator.clipboard.writeText(deviceId).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  async function updateVoiceCommands(enabled: boolean) {
    if (!isNative) return;
    try {
      const result = await NativeSpeech.setVoiceCommandsEnabled({ enabled });
      setNativeStatus((current) => ({ ...current, voiceCommandsEnabled: result.enabled }));
    } catch { /* the displayed value remains unchanged */ }
  }

  async function openPermissions() {
    if (isNative) await NativeSpeech.openAppSettings().catch(() => undefined);
  }

  async function deleteAccount() {
    if (!token) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await apiRequest("/auth/delete-account", { method: "POST", body: JSON.stringify({ password, confirmation }) }, token);
      logout();
      navigate("/", { replace: true });
    } catch (caught) {
      setDeleteError(caught instanceof ApiError ? caught.message : "No se pudo eliminar la cuenta");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-stack profile-page settings-page">
      <section className="settings-identity">
        <div className="avatar small"><UserRound size={25} /></div>
        <div><span>Cuenta LingoSound</span><strong>{account?.user.name}</strong><small>{account?.user.email}</small></div>
        <BadgeCheck size={22} />
      </section>

      <section className="membership-card">
        <div className="membership-top"><span><Sparkles size={17} /> MEMBRESÍA XR4</span><b><i className="status-dot active" /> Activada</b></div>
        <h1>Traducción protegida para tu XR4</h1>
        <div className="membership-metrics">
          <div><strong>{speechRemaining}/{speechLimit}</strong><span>Conversaciones disponibles</span></div>
          <div><strong>{account?.limits.voiceSessionMinutes ?? 15} min</strong><span>Máximo por conversación</span></div>
          <div><strong>{usagePercent}%</strong><span>Uso mensual</span></div>
        </div>
        <div className="membership-progress"><span style={{ width: `${usagePercent}%` }} /></div>
        <small>Los límites se renuevan automáticamente y evitan cargos inesperados.</small>
      </section>

      <SettingsGroup title="Dispositivo XR4">
        <SettingsRow icon={Bluetooth} title="Salida de audio" value={nativeStatus.bluetoothConnected ? nativeStatus.audioOutputs[0] || "Bluetooth conectado" : "Se verifica al iniciar audio"} status={nativeStatus.bluetoothConnected ? "success" : undefined} onClick={() => setShowDevice((value) => !value)} />
        {showDevice && <div className="settings-inline-help"><strong>Bluetooth administrado por iPhone</strong><p>Conectá el XR4 en Ajustes › Bluetooth. LingoSound selecciona esa salida automáticamente cuando iniciás una conversación.</p></div>}
        <SettingsRow icon={Smartphone} title="ID del dispositivo vinculado" value={shortDeviceId} onClick={() => void copyDeviceId()} trailing={copied ? "Copiado" : <Copy size={17} />} />
        <SettingsRow icon={QrCode} title="Activación XR4" value={`Vinculada ${account?.license ? new Date(account.license.activatedAt).toLocaleDateString("es-AR") : ""}`} />
        <SettingsRow icon={Link2} title="Vincular otro dispositivo" value="Transferencia protegida" href="mailto:ventas@lingosound.com?subject=Transferir%20mi%20LingoSound%20XR4" />
        <div className="settings-toggle-row"><Mic2 size={21} /><span><strong>Activación por voz</strong><small>{isNative ? "Iniciar o detener sin tocar el teléfono" : "Disponible en la aplicación para iPhone"}</small></span><label className="switch"><input type="checkbox" checked={isNative && nativeStatus.voiceCommandsEnabled} disabled={!isNative} onChange={(event) => void updateVoiceCommands(event.target.checked)} /><i /></label></div>
      </SettingsGroup>

      <SettingsGroup title="Aplicación">
        <SettingsRow icon={Languages} title="Idioma de la aplicación" value="Español (Argentina)" />
        <SettingsRow icon={ShieldCheck} title="Gestión de permisos" value={isNative ? "Abrir ajustes de iPhone" : "Micrófono desde el navegador"} onClick={() => void openPermissions()} />
        <SettingsRow icon={Network} title="Nodo de servicio" value="Automático · el más cercano" />
        <SettingsRow icon={CloudOff} title="Almacenamiento de audio" value="Desactivado · no guardamos audio" status="success" />
      </SettingsGroup>

      <SettingsGroup title="Ayuda y acerca de">
        <SettingsRow icon={BookOpen} title="Guía del usuario" value="Cómo usar cada modo" to="/app/guide" />
        <SettingsRow icon={CircleHelp} title="Servicio y asistencia" value="ventas@lingosound.com" href="mailto:ventas@lingosound.com?subject=Ayuda%20con%20LingoSound%20XR4" />
        <SettingsRow icon={MessageSquareText} title="Enviar comentarios" value="Ayudanos a mejorar" href="mailto:ventas@lingosound.com?subject=Comentarios%20sobre%20LingoSound" />
        <SettingsRow icon={Globe2} title="Privacidad y condiciones" value="Información legal" to="/privacy" />
        <SettingsRow icon={Settings2} title="Versión de la aplicación" value="LingoSound 3.0 · XR4 Pilot" />
      </SettingsGroup>

      <SettingsGroup title="Cuenta">
        <SettingsRow icon={UserRound} title="Cuenta actual" value={account?.user.email ?? ""} />
        <button className="settings-action-row" onClick={leave}><LogOut size={21} /><span>Cerrar sesión</span><ChevronRight size={18} /></button>
        <button className="settings-action-row danger" onClick={() => setShowDelete((value) => !value)}><Trash2 size={21} /><span>Desactivar y eliminar cuenta</span><ChevronRight size={18} /></button>
        {showDelete && <div className="delete-account-panel"><strong>Esta acción es permanente</strong><p>Cancelará el acceso, borrará tus datos y desactivará el XR4 asociado. El código no podrá reutilizarse.</p><label>Contraseña<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label><label>Escribí ELIMINAR<input value={confirmation} onChange={(event) => setConfirmation(event.target.value.toUpperCase())} /></label>{deleteError && <div className="form-error" role="alert">{deleteError}</div>}<button type="button" className="button destructive" disabled={deleting || confirmation !== "ELIMINAR" || !password} onClick={() => void deleteAccount()}>{deleting ? "Eliminando…" : "Eliminar definitivamente"}</button></div>}
      </SettingsGroup>
    </div>
  );
}

function SettingsGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="settings-group"><h2>{title}</h2><div className="settings-panel">{children}</div></section>;
}

interface SettingsRowProps {
  icon: React.ComponentType<{ size?: number }>;
  title: string;
  value?: string;
  status?: "success";
  onClick?: () => void;
  href?: string;
  to?: string;
  trailing?: React.ReactNode;
}

function SettingsRow({ icon: Icon, title, value, status, onClick, href, to, trailing }: SettingsRowProps) {
  const content = <><Icon size={21} /><span><strong>{title}</strong>{value && <small className={status === "success" ? "success" : ""}>{value}</small>}</span>{trailing ?? ((onClick || href || to) && <ChevronRight size={18} />)}</>;
  const className = "settings-row";
  if (to) return <Link className={className} to={to}>{content}</Link>;
  if (href) return <a className={className} href={href}>{content}</a>;
  if (onClick) return <button type="button" className={className} onClick={onClick}>{content}</button>;
  return <div className={className}>{content}</div>;
}
